// NOTE: intentionally NOT a 'use client' module. ProfessionalsService extends
// the isomorphic ApiService and is consumed by server components (the public
// advocate-blog pages call `getProfessionalById` during render). Marking it
// 'use client' makes its exports client-reference proxies on the server, so a
// server-side call throws. All browser access is `typeof window`-guarded.
/**
 * API service for professionals feature.
 *
 * Epic: #917 Lawyer Profiles & Client-Lawyer Matching System
 * Issue: #920 Lawyer profile UI components
 */

import { BACKEND_URL } from '@/lib/config';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
import {
  type CaseTransitionOutcome,
  classifyCaseTransitionOutcome,
} from '@/components/inquiries/caseLifecycle.helpers';
import {
  type SharedDocumentProbeResult,
  classifySharedDocumentProbe,
} from '@/components/inquiries/inquiryDetail.helpers';
import type {
  AutocompleteResponse,
  BulkMatchCreate,
  BulkMatchCreateResponse,
  CaseStatus,
  CityCountsResponse,
  ClaimDemandSignalResponse,
  ClaimProfileRequest,
  ClientIdentityRevealLevel,
  CompleteClaimRequest,
  CompleteClaimResponse,
  CompleteOptOutRequest,
  CompleteOptOutResponse,
  CreatePortalResponse,
  FeaturedClickRequest,
  FindMatchingProfessionalsRequest,
  FindMatchingProfessionalsResponse,
  InitiateClaimRequest,
  InitiateClaimResponse,
  InitiateOptOutRequest,
  InitiateOptOutResponse,
  InvoiceListResponse,
  LawyerInboxParams,
  LawyerInboxResponse,
  LawyerOfferCreate,
  LawyerOfferListResponse,
  LawyerOfferRead,
  LawyerOfferRetractResponse,
  LocationAutocompleteResult,
  MapDataResponse,
  MapPointsResponse,
  MatchCreate,
  MatchCreateResponse,
  MatchPreferences,
  MatchPreferencesUpdate,
  MatchStatus,
  MatchUpdate,
  NotificationPreferences,
  NotificationPreferencesUpdate,
  ProfessionType,
  ProfessionTypesResponse,
  ProfessionalAnalytics,
  ProfessionalMatch,
  ProfessionalRead,
  ProfessionalSearchRequest,
  ProfessionalSearchResponse,
  ProfessionalStatus,
  ProfessionalUpdate,
  RevealIdentityResponse,
  SearchProfessionalsResponse,
  SmartMatchResponse,
  SmartMatchSearchParams,
  SortBy,
  SpecInference,
  SubscriptionTier,
  TaxonomyResponse,
  VerifyClaimTokenRequest,
  VerifyClaimTokenResponse,
  VerifyOptOutRequest,
  VerifyOptOutResponse,
} from '@/types/professionals-types';
import axios from 'axios';
import ApiService, { type ApiResponse } from './api-services';

interface SearchParams {
  profession_type?: ProfessionType | undefined;
  city?: string | undefined;
  location_type?:
    | 'city'
    | 'region'
    | 'radius'
    | 'district'
    | 'multi'
    | undefined;
  region_code?: string | undefined;
  district?: string | undefined;
  latitude?: number | undefined;
  longitude?: number | undefined;
  radius_km?: number | undefined;
  cities?: string[] | undefined;
  region_codes?: string[] | undefined;
  districts?: string[] | undefined;
  specializations?: string[] | undefined;
  languages?: string[] | undefined;
  name?: string | undefined;
  accepts_new_clients?: boolean | undefined;
  subscription_tier?: SubscriptionTier | undefined;
  registry_statuses?: ProfessionalStatus[] | undefined;
  sort_by?: SortBy | undefined;
  page?: number | undefined;
  page_size?: number | undefined;
}

class ProfessionalsService extends ApiService {
  constructor() {
    super(BACKEND_URL.PROFESSIONALS);
  }

  /**
   * Get available profession types.
   * This should be cached by the caller (1 day TTL recommended).
   */
  getProfessionTypes(): ApiResponse<ProfessionTypesResponse> {
    return this.getData('/profession-types');
  }

  /**
   * Real-time autocomplete search for professionals by name.
   * Optimized for typeahead with minimal data returned.
   *
   * @param query - Search query (min 2 characters)
   * @param professionType - Optional filter by profession type
   * @param limit - Max results (1-20, default 10)
   */
  autocomplete(
    query: string,
    professionType?: ProfessionType,
    limit = 10
  ): ApiResponse<AutocompleteResponse> {
    const params = new URLSearchParams({ q: query });
    if (professionType) {
      params.append('profession_type', professionType);
    }
    if (limit !== 10) {
      params.append('limit', String(limit));
    }
    return this.getData(`/autocomplete?${params.toString()}`);
  }

  /**
   * Search professionals with filters.
   */
  searchProfessionals(
    params: SearchParams = {}
  ): ApiResponse<SearchProfessionalsResponse> {
    const queryParams = new URLSearchParams();

    if (params.profession_type) {
      queryParams.append('profession_type', params.profession_type);
    }
    if (params.city) {
      queryParams.append('city', params.city);
    }
    if (params.location_type) {
      queryParams.append('location_type', params.location_type);
    }
    if (params.region_code) {
      queryParams.append('region_code', params.region_code);
    }
    if (params.district) {
      queryParams.append('district', params.district);
    }
    if (params.latitude !== undefined) {
      queryParams.append('latitude', String(params.latitude));
    }
    if (params.longitude !== undefined) {
      queryParams.append('longitude', String(params.longitude));
    }
    if (params.radius_km !== undefined) {
      queryParams.append('radius_km', String(params.radius_km));
    }
    if (params.name) {
      queryParams.append('name', params.name);
    }
    if (params.accepts_new_clients !== undefined) {
      queryParams.append(
        'accepts_new_clients',
        String(params.accepts_new_clients)
      );
    }
    if (params.subscription_tier) {
      queryParams.append('subscription_tier', params.subscription_tier);
    }
    if (params.sort_by && params.sort_by !== 'default') {
      queryParams.append('sort_by', params.sort_by);
    }
    if (params.page) {
      queryParams.append('page', String(params.page));
    }
    if (params.page_size) {
      queryParams.append('page_size', String(params.page_size));
    }
    // Handle arrays
    if (params.specializations?.length) {
      params.specializations.forEach((s) =>
        queryParams.append('specializations', s)
      );
    }
    if (params.languages?.length) {
      params.languages.forEach((l) => queryParams.append('languages', l));
    }
    if (params.registry_statuses?.length) {
      params.registry_statuses.forEach((s) =>
        queryParams.append('registry_statuses', s)
      );
    }
    if (params.cities?.length) {
      params.cities.forEach((c) => queryParams.append('cities', c));
    }
    if (params.region_codes?.length) {
      params.region_codes.forEach((r) => queryParams.append('region_codes', r));
    }
    if (params.districts?.length) {
      params.districts.forEach((d) => queryParams.append('districts', d));
    }
    const queryString = queryParams.toString();
    const path = queryString ? `?${queryString}` : '';

    return this.getData(path);
  }

  /**
   * Smart match: AI-powered topic search. Analyzes the question to extract
   * legal areas and returns professionals ranked by relevance score.
   */
  smartMatch(params: SmartMatchSearchParams): ApiResponse<SmartMatchResponse> {
    return this.postData('/match/smart', params);
  }

  /**
   * Directory search: name results + topic enrichment merged server-side into
   * one RRF-ordered payload (``professionals`` is already in final order).
   * Replaces the old two-phase ``/search/unified`` → ``/search/topic`` handshake.
   */
  directorySearch(
    params: ProfessionalSearchRequest
  ): ApiResponse<ProfessionalSearchResponse> {
    return this.postData('/search', params);
  }

  /**
   * Get counts of professionals grouped by city for current filters.
   * @deprecated Use getMapData() instead for combined fetch.
   */
  getCityCounts(params: SearchParams = {}): ApiResponse<CityCountsResponse> {
    const path = this._buildFilterPath('/city-counts', params);
    return this.getData(path);
  }

  /**
   * Get lightweight coordinates for all matching professionals (map display).
   * No pagination — returns up to 5000 points.
   * @deprecated Use getMapData() instead for combined fetch.
   */
  getMapPoints(params: SearchParams = {}): ApiResponse<MapPointsResponse> {
    const path = this._buildFilterPath('/map-points', params, true);
    return this.getData(path);
  }

  /**
   * Get map points AND city counts in a single request.
   * Replaces separate getCityCounts() + getMapPoints() calls.
   */
  getMapData(params: SearchParams = {}): ApiResponse<MapDataResponse> {
    const path = this._buildFilterPath('/map-data', params, true);
    return this.getData(path);
  }

  /** Build query string from common filter params. */
  private _buildFilterPath(
    endpoint: string,
    params: SearchParams,
    includeRegistryStatuses = false
  ): string {
    const q = new URLSearchParams();

    if (params.profession_type)
      q.append('profession_type', params.profession_type);
    if (params.city) q.append('city', params.city);
    if (params.location_type) q.append('location_type', params.location_type);
    if (params.region_code) q.append('region_code', params.region_code);
    if (params.district) q.append('district', params.district);
    if (params.latitude !== undefined)
      q.append('latitude', String(params.latitude));
    if (params.longitude !== undefined)
      q.append('longitude', String(params.longitude));
    if (params.radius_km !== undefined)
      q.append('radius_km', String(params.radius_km));
    if (params.name) q.append('name', params.name);
    if (params.accepts_new_clients !== undefined)
      q.append('accepts_new_clients', String(params.accepts_new_clients));
    if (params.subscription_tier)
      q.append('subscription_tier', params.subscription_tier);
    if (params.specializations?.length)
      params.specializations.forEach((s) => q.append('specializations', s));
    if (params.languages?.length)
      params.languages.forEach((l) => q.append('languages', l));
    if (params.cities?.length)
      params.cities.forEach((c) => q.append('cities', c));
    if (params.region_codes?.length)
      params.region_codes.forEach((r) => q.append('region_codes', r));
    if (params.districts?.length)
      params.districts.forEach((d) => q.append('districts', d));
    if (includeRegistryStatuses && params.registry_statuses?.length)
      params.registry_statuses.forEach((s) => q.append('registry_statuses', s));

    const qs = q.toString();
    return qs ? `${endpoint}?${qs}` : endpoint;
  }

  /**
   * Get a single professional by ID.
   * @deprecated Use getProfessionalById instead.
   */
  getProfessional(professionalId: string): ApiResponse<ProfessionalRead> {
    return this.getProfessionalById(professionalId);
  }

  /**
   * Find matching professionals based on legal areas and context.
   * Used for research session integration.
   */
  findMatching(
    data: FindMatchingProfessionalsRequest
  ): ApiResponse<FindMatchingProfessionalsResponse> {
    return this.postData('/match', data);
  }

  /**
   * Get the current client's saved match preferences (global geo + language
   * context). Returns neutral defaults (location_mode=null,
   * accepts_new_clients_only=true) when none are set. Backed by
   * client_match_preferences (issue #4988 Phase 4a). The same prefs pre-fill
   * both the directory and the chat find-lawyers flow, and the backend's
   * /match applies them when a request carries no explicit geo (Phase 4b).
   */
  getMatchPreferences(): ApiResponse<MatchPreferences> {
    return this.getData('/match-preferences');
  }

  /**
   * Upsert the current client's match preferences. ``location_mode: null``
   * clears the geo restriction. Persisting a geo choice (e.g. the chat
   * "Zúžiť na okolie" prompt) makes it the global default so subsequent
   * matches — chat or directory — are pre-filled the same way.
   */
  updateMatchPreferences(
    update: MatchPreferencesUpdate
  ): ApiResponse<MatchPreferences> {
    return this.putData('/match-preferences', update);
  }

  /**
   * Record a click on the Profil+ featured slot or an adjacent organic
   * row (issue #3686 — regulator-readiness audit log).
   *
   * The endpoint is idempotent on ``(impression_id, click_position)`` so
   * repeat posts (e.g. from a flaky network) collapse to one row. The
   * caller treats this as fire-and-forget — the navigation that follows
   * the click does not wait on the network.
   */
  recordFeaturedClick(data: FeaturedClickRequest): ApiResponse<null> {
    return this.postData('/match/featured-click', data);
  }

  /**
   * Create a new client-professional match.
   *
   * Returns the slim ``MatchCreateResponse`` (id + status + created_at)
   * — the full ProfessionalMatch shape is intentionally not surfaced
   * by the API on the success path; consumers only need the id for
   * navigation and the status for chip rendering.
   */
  createMatch(match: MatchCreate): ApiResponse<MatchCreateResponse> {
    return this.postData('/matches', match);
  }

  /**
   * Contact several advocates in one click — bulk inquiry (#6046).
   *
   * One shared inquiry/intake fanned out to a client-curated shortlist
   * (≤ ``BULK_CONTACT_MAX_RECIPIENTS``). The backend loops the identical
   * single-recipient pipeline server-side, so the per-professional 30-day
   * dedup, per-user rate-limit, lawyer notification, and #5835 consent log
   * are all enforced PER recipient. Always resolves 200 with a per-advocate
   * outcome list — partial success (some ``sent``, some ``already_contacted``)
   * is the norm, not an error.
   */
  createMatchesBulk(
    bulk: BulkMatchCreate
  ): ApiResponse<BulkMatchCreateResponse> {
    return this.postData('/matches/bulk', bulk);
  }

  /**
   * Get current user's matches (as client).
   */
  getMyMatches(status?: MatchStatus): ApiResponse<ProfessionalMatch[]> {
    const path = status ? `/matches/me?match_status=${status}` : '/matches/me';
    return this.getData(path);
  }

  /**
   * Get matches addressed to the professional this user has claimed.
   *
   * Backs the lawyer's "Doručené dopyty" inbox (Phase 2 of #3557). The
   * backend resolves the user → professional mapping inside the SQL JOIN
   * (``professionals.user_id = $1``) so the endpoint never accepts a
   * professional_id parameter — authorization is RLS-natural.
   *
   * @returns ``LawyerInboxResponse`` with paginated matches + has_more.
   *          Empty list (NOT 404) when the user has no claimed profile.
   */
  getLawyerInbox(
    params: LawyerInboxParams = {}
  ): ApiResponse<LawyerInboxResponse> {
    const queryParts: string[] = [];
    if (params.page !== undefined) queryParts.push(`page=${params.page}`);
    if (params.page_size !== undefined)
      queryParts.push(`page_size=${params.page_size}`);
    if (params.match_status)
      queryParts.push(`match_status=${params.match_status}`);
    const query = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    return this.getData(`/matches/professional${query}`);
  }

  /**
   * Get a single match by id, gated to participants only (#3557 P2.3).
   *
   * Backs the inquiry detail page. Replaces the previous "fetch full
   * inbox + filter client-side" pattern, which made the matchId URL
   * the only thing standing between a stranger and the conversation
   * contents. The backend collapses "doesn't exist" and "you can't
   * read this" into the same 404 to prevent id enumeration.
   */
  getMatch(matchId: string): ApiResponse<ProfessionalMatch> {
    return this.getData(`/matches/${matchId}`);
  }

  /**
   * Get a single professional by UUID.
   */
  getProfessionalById(professionalId: string): ApiResponse<ProfessionalRead> {
    return this.getData(`/${professionalId}`);
  }

  /**
   * Get a single professional by slug.
   */
  getProfessionalBySlug(slug: string): ApiResponse<ProfessionalRead> {
    return this.getData(`/slug/${slug}`);
  }

  /**
   * Get a professional by UUID or slug — routes to the correct endpoint.
   */
  getProfessionalByIdOrSlug(idOrSlug: string): ApiResponse<ProfessionalRead> {
    return UUID_RE.test(idOrSlug)
      ? this.getProfessionalById(idOrSlug)
      : this.getProfessionalBySlug(idOrSlug);
  }

  /**
   * Update match status.
   */
  updateMatchStatus(
    matchId: string,
    update: MatchUpdate
  ): ApiResponse<ProfessionalMatch> {
    return this.patchData(`/matches/${matchId}/status`, update);
  }

  /**
   * Add a message to a match conversation.
   */
  addMatchMessage(
    matchId: string,
    content: string
  ): ApiResponse<ProfessionalMatch> {
    return this.postData(`/matches/${matchId}/messages`, { content });
  }

  /**
   * Reveal the client's identity to the advocate for a match (#6038).
   *
   * Client-only. The reveal level must be higher than the current one
   * (none < name < contact — monotonic). Once revealed, the level cannot
   * be decreased.
   *
   * DRAFT — pending @citbaj / DPO sign-off. Endpoint is OFF by default
   * (IDENTITY_REVEAL_ENABLED=false in backend settings) and returns 404
   * until the compliance sign-off checklist is satisfied.
   * Merging != ratifying; not for deploy until sign-off.
   *
   * @param matchId - UUID of the match for which to reveal.
   * @param toLevel - The requested reveal level ('name' or 'contact').
   * @returns The updated match_id + new client_identity_revealed level.
   */
  revealIdentity(
    matchId: string,
    toLevel: ClientIdentityRevealLevel
  ): ApiResponse<RevealIdentityResponse> {
    return this.patchData(`/matches/${matchId}/reveal`, {
      to_level: toLevel,
    });
  }

  /**
   * Transition a case through its lifecycle (#6019 / #6317 C1).
   *
   * Backs the inquiry-detail "Začať spoluprácu" (→ in_progress) and "Ukončiť
   * prípad" (→ done) actions. Reaching `done` unlocks the anonymized review
   * funnel (#5088 / #5854).
   *
   * Authorization + the per-party state machine are enforced SERVER-SIDE — the
   * acting role is resolved from the auth user against the match, never trusted
   * from the body. The frontend only renders the buttons the backend would
   * accept; this method re-classifies the response into a typed outcome so the
   * caller branches on `.status` rather than on locale-dependent error text.
   *
   *   200 → { status: 'success', caseStatus, caseDoneAt, reviewAvailable }
   *   409 IDENTITY_REVEAL_REQUIRED → { status: 'identity_reveal_required' }
   *   409 ILLEGAL_CASE_TRANSITION  → { status: 'illegal_transition' }
   *   409 CASE_TRANSITION_CONFLICT → { status: 'conflict' }
   *   anything else                → { status: 'error' }
   *
   * The global axios interceptor (`services/api-services.ts`) rejects 409 so the
   * `catch` here receives a real `AxiosError` whose `response.status`/`.data` we
   * inspect structurally.
   *
   * @param matchId  UUID of the match/mandate record.
   * @param toStatus Requested target case status.
   */
  async transitionCaseStatus(
    matchId: string,
    toStatus: CaseStatus
  ): Promise<CaseTransitionOutcome> {
    try {
      const body = await this.patchData(`/matches/${matchId}/case-status`, {
        case_status: toStatus,
      });
      return classifyCaseTransitionOutcome(200, body);
    } catch (err) {
      const httpStatus = axios.isAxiosError(err)
        ? err.response?.status
        : undefined;
      const errBody = axios.isAxiosError(err) ? err.response?.data : undefined;
      return classifyCaseTransitionOutcome(httpStatus, errBody);
    }
  }

  /**
   * Claim a professional profile.
   */
  claimProfile(data: ClaimProfileRequest): ApiResponse<ProfessionalRead> {
    return this.postData('/claim', data);
  }

  /**
   * Update a claimed professional profile.
   */
  updateProfile(
    professionalId: string,
    update: ProfessionalUpdate
  ): ApiResponse<ProfessionalRead> {
    return this.patchData(`/profile/${professionalId}`, update);
  }

  /**
   * Get current user's claimed professional profile.
   */
  getMyProfile(): ApiResponse<ProfessionalRead> {
    return this.getData('/my-profile');
  }

  /**
   * Get matches for current user's professional profile (as professional).
   */
  getMyProfessionalMatches(
    status?: MatchStatus
  ): ApiResponse<ProfessionalMatch[]> {
    const path = status
      ? `/my-profile/matches?match_status=${status}`
      : '/my-profile/matches';
    return this.getData(path);
  }

  /**
   * Full GDPR deletion of the current user's professional profile.
   * Clears all data, blacklists registry_number, and deletes matches.
   */
  deleteMyProfile(): ApiResponse<{
    deleted: boolean;
    professional_id: string;
  }> {
    return this.deleteData('/my-profile', {});
  }

  /**
   * Delete all self-reported contact overrides for the current user's
   * professional profile (#4684 PR 2c).
   *
   * Returns 200 {deleted: true, professional_id} on success, 404 if the
   * user has no claimed profile. Idempotent — safe to call even when no
   * overrides exist. Only meaningful while downgraded (non-paid tier); the
   * UI should gate the "Delete now" CTA on downgraded_at IS NOT NULL.
   */
  deleteContactOverrides(): ApiResponse<{
    deleted: boolean;
    professional_id: string;
  }> {
    return this.deleteData('/my-profile/contact-overrides', {});
  }

  // ==========================================================================
  // EMAIL VERIFICATION CLAIM FLOW (Issue #923)
  // ==========================================================================

  /**
   * Initiate email claim verification.
   * Sends verification email to professional's SAK registry email.
   */
  initiateEmailClaim(
    data: InitiateClaimRequest
  ): ApiResponse<InitiateClaimResponse> {
    return this.postData('/claim/initiate', data);
  }

  /**
   * Public per-lawyer demand signal for the claim landing page (#5369).
   * Pre-auth — the claim page is hit before login. Returns the non-PII
   * 30-day impression count + concrete-threshold flag the landing hero uses
   * to mirror the claim-activation email's conditional demand signal.
   */
  getClaimDemandSignal(
    professionalId: string
  ): ApiResponse<ClaimDemandSignalResponse> {
    return this.postData('/claim/demand-signal', {
      professional_id: professionalId,
    });
  }

  /**
   * Verify claim token from email link.
   * Returns professional info if token is valid.
   */
  verifyClaimToken(
    data: VerifyClaimTokenRequest
  ): ApiResponse<VerifyClaimTokenResponse> {
    return this.postData('/claim/verify', data);
  }

  /**
   * Complete claim after token verification.
   * Requires authenticated user and terms acceptance.
   */
  completeClaim(
    data: CompleteClaimRequest
  ): ApiResponse<CompleteClaimResponse> {
    return this.postData('/claim/complete', data);
  }

  // ==========================================================================
  // EMAIL VERIFICATION OPT-OUT FLOW (Issue #924)
  // ==========================================================================

  /**
   * Initiate email-verified opt-out.
   * Sends verification email to professional's registry email.
   */
  initiateOptOut(
    data: InitiateOptOutRequest
  ): ApiResponse<InitiateOptOutResponse> {
    return this.postData('/opt-out/initiate', data);
  }

  /**
   * Verify opt-out token from email link.
   * Returns professional info and opt-out type if token is valid.
   */
  verifyOptOutToken(
    data: VerifyOptOutRequest
  ): ApiResponse<VerifyOptOutResponse> {
    return this.postData('/opt-out/verify', data);
  }

  /**
   * Complete opt-out after token verification.
   * Requires user confirmation.
   */
  completeOptOut(
    data: CompleteOptOutRequest
  ): ApiResponse<CompleteOptOutResponse> {
    return this.postData('/opt-out/complete', data);
  }

  /**
   * Get all Slovak regions (kraje) with Slovak and English names.
   * This should be cached by the caller (1 day TTL recommended).
   */
  /**
   * Location typeahead: returns grouped regions, districts, and cities matching the query.
   * Diacritic-insensitive — "zvole" matches "Zvolen" and "Zvolenská Slatina".
   * Call with minimum 2 characters; debounce on the caller side.
   */
  async locationAutocomplete(q: string): Promise<LocationAutocompleteResult> {
    try {
      const params = new URLSearchParams({ q });
      const response = await this.getData(
        `/location-autocomplete?${params.toString()}`
      );
      return response as unknown as LocationAutocompleteResult;
    } catch {
      return { regions: [], districts: [], cities: [] };
    }
  }

  async getRegions(): Promise<
    Array<{ code: string; name_sk: string; name_en: string }>
  > {
    try {
      const response = await this.getData('/regions');
      return response as unknown as Array<{
        code: string;
        name_sk: string;
        name_en: string;
      }>;
    } catch {
      return [];
    }
  }

  /**
   * Fetch accurate ZBGIS boundary for a Slovak region from the PostGIS backend.
   * Returns null on error so the caller can fall back to the static file.
   */
  async getRegionBoundary(regionCode: string): Promise<GeoJSON.Feature | null> {
    try {
      const response = await this.getData(`/regions/${regionCode}/boundary`);
      // ApiService interceptor already unwraps axios response.data, so
      // `response` IS the GeoJSON Feature body — no extra `.data` access needed.
      return response as unknown as GeoJSON.Feature;
    } catch {
      return null;
    }
  }

  async getDistrictBoundary(
    districtName: string
  ): Promise<GeoJSON.Feature | null> {
    try {
      const encoded = encodeURIComponent(districtName);
      const response = await this.getData(`/districts/${encoded}/boundary`);
      return response as unknown as GeoJSON.Feature;
    } catch {
      return null;
    }
  }

  async logSearchEvent(event: {
    // 'click' = card navigation to the detail page (browse intent).
    // 'contact_click' = the prominent "Kontaktovať" CTA on a result card
    //   (high contact intent — #5371, the DB-side stage of the #5370 funnel).
    //   Kept DISTINCT from 'click' so the claim-activation funnel can slot a
    //   contact-click stage between `impression` and `pending_inquiry`.
    // 'profile_view' = detail-page view.
    event_type: 'click' | 'contact_click' | 'profile_view';
    professional_id: string;
    search_query?: string | null;
    search_intent?: string | null;
    position?: number | null;
    score?: number | null;
  }): Promise<void> {
    try {
      await this.postData('/search/events', event);
    } catch {
      // Non-critical — don't break UX on logging failure
    }
  }

  async getCityBoundary(cityName: string): Promise<GeoJSON.Feature | null> {
    try {
      const encoded = encodeURIComponent(cityName);
      const response = await this.getData(`/cities/${encoded}/boundary`);
      return response as unknown as GeoJSON.Feature;
    } catch {
      // 404 = not yet in sk_obec (cron hasn't processed it) — caller falls back to halo
      return null;
    }
  }

  /**
   * Create a Stripe checkout session for a professional tier upgrade.
   * Redirects to Stripe-hosted checkout page.
   */
  async createCheckout(
    professionalId: string,
    tier: 'profile',
    billingInterval: 'monthly' | 'annual' = 'monthly'
  ): Promise<{ checkout_url: string; session_id: string }> {
    const response = await this.postData(`/${professionalId}/checkout`, {
      tier,
      billing_interval: billingInterval,
    });
    return response as unknown as { checkout_url: string; session_id: string };
  }

  /**
   * Get analytics for a professional profile (paid tiers only).
   */
  async getAnalytics(
    professionalId: string,
    periodDays = 30
  ): Promise<ProfessionalAnalytics> {
    const response = await this.getData(
      `/${professionalId}/analytics?period_days=${periodDays}`
    );
    return response as unknown as ProfessionalAnalytics;
  }

  /**
   * Get subscription status for a professional (owner or admin).
   * Returns current tier, Stripe customer id, and premium flag.
   */
  async getSubscriptionStatus(professionalId: string): Promise<{
    professional_id: string;
    subscription_tier: SubscriptionTier;
    is_premium: boolean;
    stripe_customer_id: string | null;
  }> {
    const response = await this.getData(`/${professionalId}/subscription`);
    return response as unknown as {
      professional_id: string;
      subscription_tier: SubscriptionTier;
      is_premium: boolean;
      stripe_customer_id: string | null;
    };
  }

  /**
   * Get notification preferences for the current user's professional profile.
   */
  getNotificationPreferences(): ApiResponse<NotificationPreferences> {
    return this.getData('/my-profile/notifications');
  }

  /**
   * Update notification preferences for the current user's professional profile.
   * Only non-null fields are updated.
   */
  updateNotificationPreferences(
    update: NotificationPreferencesUpdate
  ): ApiResponse<NotificationPreferences> {
    return this.patchData('/my-profile/notifications', update);
  }

  /**
   * Create a Stripe Customer Portal session for owner to manage billing.
   * Returns a short-lived URL that should be opened in a new tab.
   */
  createCustomerPortalSession(
    professionalId: string,
    returnUrl?: string
  ): ApiResponse<CreatePortalResponse> {
    return this.postData(
      `/${professionalId}/portal`,
      returnUrl ? { return_url: returnUrl } : {}
    );
  }

  /**
   * List Stripe invoices for a professional (owner-only).
   */
  listInvoices(professionalId: string): ApiResponse<InvoiceListResponse> {
    return this.getData(`/${professionalId}/invoices`);
  }

  // ==========================================================================
  // SPEC CURATION (#5509 / #5510)
  // ==========================================================================

  /**
   * Fetch the canonical SAK specialization vocabulary.
   * Used by the public directory filter (useSpecializations hook).
   * Not used by the practice-area picker (which uses getTaxonomy instead).
   *
   * GET /professionals/spec-vocabulary?q=...&limit=...&offset=...
   * → { status, data: { specializations: string[] } }
   */
  getSpecVocabulary(params?: {
    q?: string;
    limit?: number;
    offset?: number;
  }): ApiResponse<{ specializations: string[] }> {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.offset != null) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return this.getData(`/spec-vocabulary${query ? `?${query}` : ''}`);
  }

  // ==========================================================================
  // PRACTICE AREAS / TAXONOMY (Stage-2b)
  // ==========================================================================

  /**
   * Fetch the hierarchical practice-area taxonomy for the picker UI.
   *
   * GET /professionals/taxonomy
   * → { status, data: { categories: TaxonomyCategory[] } }
   *
   * Fetched once per dashboard mount; response is stable (taxonomy changes
   * only on backend deployments). Callers may cache with a short TTL.
   */
  getTaxonomy(): ApiResponse<TaxonomyResponse> {
    return this.getData('/taxonomy');
  }

  /**
   * Overwrite the current user's selected practice areas with the given leaf ids.
   *
   * PUT /professionals/my-profile/practice-areas
   * Body: { ids: string[] }
   * → returns the full updated ProfessionalRead in `.data`
   *
   * Sends the FULL set every time (last-write-wins). The caller derives the new
   * set by toggling the clicked leaf id in/out of the existing set and passes
   * the result here. Follows the same await-then-onUpdated pattern as the old
   * curateSpecialization — no optimistic replica.
   *
   * Error shapes:
   *   404  — no claimed profile for this user
   *   422  — one or more ids are not valid leaf ids in the taxonomy
   */
  setPracticeAreas(ids: string[]): ApiResponse<ProfessionalRead> {
    return this.putData('/my-profile/practice-areas', { ids });
  }

  // ==========================================================================
  // INFERRED SPECS — the third spec category (#5540)
  // ==========================================================================

  /**
   * List the current user's PENDING inferred-spec candidates (the
   * "z popisu praxe → confirm?" hint). Owner-only, tier-blind. Returns an
   * empty list (not a 404) when there is no claimed profile or nothing pending.
   *
   * GET /professionals/my-profile/spec-inferences
   * → { status, data: { inferences: SpecInference[] } }
   */
  listSpecInferences(): ApiResponse<{ inferences: SpecInference[] }> {
    return this.getData('/my-profile/spec-inferences');
  }

  /**
   * Confirm a pending inferred spec — promotes it to a curated addition
   * (#5509, full trust) and returns the full updated ProfessionalRead.
   *
   * POST /professionals/my-profile/spec-inferences/confirm  Body: { spec }
   * Error shapes: 404 (no claimed profile), 422 { error: 'inference_not_pending' }
   */
  confirmSpecInference(spec: string): ApiResponse<ProfessionalRead> {
    return this.postData('/my-profile/spec-inferences/confirm', { spec });
  }

  /**
   * Dismiss a pending inferred spec — tombstones the suggestion so
   * re-inference never resurfaces it. No effect on the lawyer's specs.
   *
   * POST /professionals/my-profile/spec-inferences/dismiss  Body: { spec }
   * Error shapes: 404 (no claimed profile), 422 { error: 'inference_not_pending' }
   */
  dismissSpecInference(
    spec: string
  ): ApiResponse<{ spec: string; dismissed: boolean }> {
    return this.postData('/my-profile/spec-inferences/dismiss', { spec });
  }

  // =========================================================================
  // LAWYER OFFER FLOW — issue #5844, Variant C marketplace
  // =========================================================================
  // DRAFT: all offer methods are behind OFFER_FLOW_ENABLED=false server-side.
  // Do NOT surface in the UI until @citbaj + DPO sign-off on:
  //   * first_take_text (legal-advice obligation / advokátske tajomstvo)
  //   * indicative_price_eur label ("orientačná cena", non-binding)
  //   * Client read policy (consent / disclosure before seeing all offers)
  //
  // SAK P2 invariant: LawyerOfferListResponse.offers is ordered by
  // submitted_at ASC. The caller MUST NOT re-sort by any other field.
  // Any re-sort is a SAK §6.2 compliance violation (no ranking / comparison).

  /**
   * Advocate submits an offer for a demand.
   *
   * P2 NEUTRALITY: submitted_at is NOT in the payload — it is set by the
   * DB (DEFAULT now()) and cannot be manipulated by the caller.
   *
   * POST /professionals/offers
   * Returns 201 with the created LawyerOfferRead.
   * Returns 403 when the advocate is not on the board for this demand.
   * Returns 409 when the advocate already submitted an offer for this demand.
   * Returns 503 when the offer-flow feature flag is off (DRAFT, pre-sign-off).
   */
  submitOffer(payload: LawyerOfferCreate): ApiResponse<LawyerOfferRead> {
    return this.postData('/offers', payload);
  }

  /**
   * Client retrieves all submitted offers for their demand.
   *
   * P2 NEUTRALITY: offers are in submitted_at ASC order. The caller MUST
   * present them to the user in exactly the returned order — any re-sort
   * is a SAK §6.2 compliance violation (prohibited comparison of advocates).
   *
   * GET /professionals/offers/{matchId}
   * Returns 403 when the calling user does not own the demand.
   * Returns 503 when the offer-flow feature flag is off (DRAFT, pre-sign-off).
   *
   * DRAFT: @citbaj / DPO should confirm the client may see all submitted offers
   * without an additional consent or disclosure step.
   */
  listOffersForDemand(matchId: string): ApiResponse<LawyerOfferListResponse> {
    return this.getData(`/offers/${matchId}`);
  }

  /**
   * Advocate retrieves their own offer for a demand.
   *
   * GET /professionals/offers/{matchId}/mine
   * Returns 404 when no offer exists for this advocate and demand.
   */
  getOwnOffer(matchId: string): ApiResponse<LawyerOfferRead> {
    return this.getData(`/offers/${matchId}/mine`);
  }

  /**
   * Advocate retracts their own submitted offer.
   *
   * DELETE /professionals/offers/{offerId}
   * Returns 404 when the offer does not exist.
   * Returns 403 when the calling user did not submit this offer.
   * Returns 409 when the offer is not in 'submitted' state.
   */
  retractOffer(offerId: string): ApiResponse<LawyerOfferRetractResponse> {
    return this.deleteData(`/offers/${offerId}`, null);
  }

  // =========================================================================
  // SHARED INTAKE DOCUMENT DELIVERY — issue #6197
  // =========================================================================

  /**
   * Upload a consented intake document for the matched lawyer to download.
   *
   * POST /professionals/matches/{matchId}/shared-document  (multipart)
   * Returns 201 { id, expires_at, original_filename }.
   * Returns 403 when caller is not the client who created the match.
   * Returns 409 when document-consent was not recorded for this match.
   * Returns 503 when doc_upload_extraction early-access feature is disabled.
   *
   * @param matchId   UUID of the match row.
   * @param file      The File object to upload (PDF or image).
   */
  uploadSharedDocument(
    matchId: string,
    file: File
  ): ApiResponse<{
    document_id: string;
    expires_at: string;
    original_filename: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    // Omit Content-Type header so axios sets multipart/form-data with boundary.
    return this.postData(`/matches/${matchId}/shared-document`, formData, {
      headers: {},
    });
  }

  /**
   * Mint a short-lived (≤1 h) signed download URL for the shared document.
   * Only the matched lawyer can call this.
   *
   * GET /professionals/matches/{matchId}/shared-document/download-url
   *   200 → { signed_url, expires_at, original_filename }
   *   404 → no document exists for this match
   *   410 → document has expired or been purged
   *   403 → caller is not the matched lawyer
   *
   * Returns a typed ``SharedDocumentProbeResult`` discriminated by ``.status``
   * rather than propagating raw HTTP error bodies. Callers must branch on the
   * ``status`` field — NOT on error-message wording — so the detection is
   * locale-independent and survives backend message changes (#6203).
   *
   * The 410 HTTP status is surfaced because the global axios interceptor
   * (``services/api-services.ts``) rejects it (alongside 402/409/500) so the
   * ``catch`` block here receives a real ``AxiosError`` whose
   * ``response.status`` we can inspect structurally.
   */
  async getSharedDocumentDownloadUrl(
    matchId: string
  ): Promise<SharedDocumentProbeResult> {
    try {
      const body = await this.getData(
        `/matches/${matchId}/shared-document/download-url`
      );
      return classifySharedDocumentProbe(200, body);
    } catch (err) {
      const httpStatus = axios.isAxiosError(err)
        ? err.response?.status
        : undefined;
      return classifySharedDocumentProbe(httpStatus, undefined);
    }
  }
}

export default new ProfessionalsService();
